import api from "../../../api/axios";
import { messageFromError } from "../../../shared/api/drfErrors";

/** Feature-local corporate Property transport (D1 endpoints). Not personal Realty. */

const base = (companyId) => `/companies/${companyId}/properties`;

export const fetchCompanyProperties = (companyId, params) =>
  api.get(`${base(companyId)}/`, { params }).then((res) => res.data);

export const createCompanyProperty = (companyId, body) =>
  api.post(`${base(companyId)}/`, body).then((res) => res.data);

export const fetchCompanyPropertiesUnassigned = (companyId, params) =>
  api
    .get(`${base(companyId)}/unassigned/`, { params })
    .then((res) => res.data);

export const fetchCompanyProperty = (companyId, propertyId) =>
  api
    .get(`${base(companyId)}/${propertyId}/`)
    .then((res) => res.data);

export const patchCompanyProperty = (companyId, propertyId, body) =>
  api
    .patch(`${base(companyId)}/${propertyId}/`, body)
    .then((res) => res.data);

export const archiveCompanyProperty = (companyId, propertyId) =>
  api
    .post(`${base(companyId)}/${propertyId}/archive/`)
    .then((res) => res.data);

export const assignCompanyProperty = (companyId, propertyId, userId) =>
  api
    .post(`${base(companyId)}/${propertyId}/assign/`, {
      user_id: userId,
    })
    .then((res) => res.data);

export const fetchCompanyPropertyParticipants = (companyId, propertyId) =>
  api
    .get(`${base(companyId)}/${propertyId}/participants/`)
    .then((res) => res.data);

export const upsertCompanyPropertyParticipant = (
  companyId,
  propertyId,
  body,
) =>
  api
    .post(`${base(companyId)}/${propertyId}/participants/`, body)
    .then((res) => res.data);

export const revokeCompanyPropertyParticipant = (
  companyId,
  propertyId,
  participantId,
) =>
  api
    .post(
      `${base(companyId)}/${propertyId}/participants/${participantId}/revoke/`,
    )
    .then((res) => res.data);

export const putCompanyPropertyLocation = (companyId, propertyId, body) =>
  api
    .put(`${base(companyId)}/${propertyId}/location/`, body)
    .then((res) => res.data);

export const putCompanyPropertyResidential = (companyId, propertyId, body) =>
  api
    .put(`${base(companyId)}/${propertyId}/residential/`, body)
    .then((res) => res.data);

export const putCompanyPropertyLand = (companyId, propertyId, body) =>
  api
    .put(`${base(companyId)}/${propertyId}/land/`, body)
    .then((res) => res.data);

export const putCompanyPropertyCommercial = (companyId, propertyId, body) =>
  api
    .put(`${base(companyId)}/${propertyId}/commercial/`, body)
    .then((res) => res.data);

export const transitionCompanyPropertyStatus = (
  companyId,
  propertyId,
  statusValue,
) =>
  api
    .post(`${base(companyId)}/${propertyId}/status/`, {
      status: statusValue,
    })
    .then((res) => res.data);

/** Work activation only — does not publish to the public catalog. */
export const activateCompanyPropertyForWork = (companyId, propertyId) =>
  transitionCompanyPropertyStatus(companyId, propertyId, "active");

/** Explicit A3P catalog publication — never emulate via /status/. */
export const publishCompanyProperty = (companyId, propertyId) =>
  api
    .post(`${base(companyId)}/${propertyId}/publish/`)
    .then((res) => res.data);

export const unpublishCompanyProperty = (companyId, propertyId) =>
  api
    .post(`${base(companyId)}/${propertyId}/unpublish/`)
    .then((res) => res.data);

export const putCompanyPropertyCooperation = (companyId, propertyId, body) =>
  api
    .put(`${base(companyId)}/${propertyId}/cooperation/`, body)
    .then((res) => res.data);

export function propertyImageUploadErrorMessage(error) {
  if (error && error.code === "PROPERTY_UPLOAD_ID_MISSING" && error.message) {
    return error.message;
  }
  const data = error && error.response && error.response.data;
  if (data && typeof data === "object") {
    if (typeof data.images === "string" && data.images.trim()) {
      return data.images.trim();
    }
    if (
      Array.isArray(data.images) &&
      typeof data.images[0] === "string" &&
      data.images[0].trim()
    ) {
      return data.images[0].trim();
    }
  }
  return messageFromError(error);
}

function assertPropertyUploadId(id) {
  const propertyId = Number(id);
  if (!Number.isInteger(propertyId) || propertyId <= 0) {
    const err = new Error(
      "Об'єкт ще не створено. Завантаження зображень недоступне.",
    );
    err.code = "PROPERTY_UPLOAD_ID_MISSING";
    return Promise.reject(err);
  }
  return Promise.resolve(propertyId);
}

export const uploadCompanyPropertyImages = (
  companyId,
  propertyId,
  files,
  kind = "image",
) =>
  assertPropertyUploadId(propertyId).then((id) => {
    const fd = new FormData();
    for (const file of files) fd.append("images", file);
    fd.append("kind", kind);
    return api
      .post(`${base(companyId)}/${id}/images/`, fd)
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data)) {
          return { images: data, errors: [] };
        }
        return {
          images: data.images || [],
          errors: data.errors || [],
        };
      });
  });

export const patchCompanyPropertyImage = (
  companyId,
  propertyId,
  imageId,
  body,
) => {
  const payload =
    body && body.image instanceof File
      ? (() => {
          const form = new FormData();
          form.append("image", body.image);
          return form;
        })()
      : body;
  return api
    .patch(`${base(companyId)}/${propertyId}/images/${imageId}/`, payload)
    .then((res) => res.data);
};

export const deleteCompanyPropertyImage = (
  companyId,
  propertyId,
  imageId,
) =>
  api
    .delete(`${base(companyId)}/${propertyId}/images/${imageId}/`)
    .then((res) => res.data);

export const reorderCompanyPropertyImages = (
  companyId,
  propertyId,
  kind,
  imageIds,
) =>
  assertPropertyUploadId(propertyId).then((id) =>
    api
      .put(`${base(companyId)}/${id}/images/reorder/`, {
        kind,
        image_ids: imageIds,
      })
      .then((res) => res.data),
  );

export const listCompanyPropertyLandParcels = (companyId, propertyId) =>
  api
    .get(`${base(companyId)}/${propertyId}/land-parcels/`)
    .then((res) => res.data);

export const createCompanyPropertyLandParcel = (
  companyId,
  propertyId,
  payload,
) =>
  api
    .post(`${base(companyId)}/${propertyId}/land-parcels/`, payload)
    .then((res) => res.data);

export const patchCompanyPropertyLandParcel = (
  companyId,
  propertyId,
  parcelId,
  payload,
) =>
  api
    .patch(
      `${base(companyId)}/${propertyId}/land-parcels/${parcelId}/`,
      payload,
    )
    .then((res) => res.data);

export const deleteCompanyPropertyLandParcel = (
  companyId,
  propertyId,
  parcelId,
) =>
  api
    .delete(`${base(companyId)}/${propertyId}/land-parcels/${parcelId}/`)
    .then((res) => res.data);

/**
 * Bound transport matching personal create/edit realty API shapes so
 * step/section widgets can reuse company-scoped endpoints without
 * forking a second form system.
 */
export function createCompanyPropertyTransport(companyId) {
  const cid = companyId;
  return {
    createProperty: (body) => createCompanyProperty(cid, body),
    fetchPropertyDetail: (id) => fetchCompanyProperty(cid, id),
    patchPropertyCore: (id, body) => patchCompanyProperty(cid, id, body),
    putPropertyLocation: (id, body) => putCompanyPropertyLocation(cid, id, body),
    putPropertyResidential: (id, body) =>
      putCompanyPropertyResidential(cid, id, body),
    putPropertyLand: (id, body) => putCompanyPropertyLand(cid, id, body),
    putPropertyCommercial: (id, body) =>
      putCompanyPropertyCommercial(cid, id, body),
    /** Work-status ACTIVE transition — not catalog publication. */
    activatePropertyForWork: (id) => activateCompanyPropertyForWork(cid, id),
    publishCompanyProperty: (id) => publishCompanyProperty(cid, id),
    unpublishCompanyProperty: (id) => unpublishCompanyProperty(cid, id),
    uploadPropertyImages: (id, files, kind) =>
      uploadCompanyPropertyImages(cid, id, files, kind),
    patchPropertyImage: (id, imageId, body) =>
      patchCompanyPropertyImage(cid, id, imageId, body),
    deletePropertyImage: (id, imageId) =>
      deleteCompanyPropertyImage(cid, id, imageId),
    reorderPropertyImages: (id, kind, imageIds) =>
      reorderCompanyPropertyImages(cid, id, kind, imageIds),
    propertyImageUploadErrorMessage,
    patchPropertyStatus: (id, statusValue) =>
      transitionCompanyPropertyStatus(cid, id, statusValue),
    putPropertyCooperation: (id, body) =>
      putCompanyPropertyCooperation(cid, id, body),
    archiveProperty: (id) => archiveCompanyProperty(cid, id),
    landParcels: {
      listLandParcels: (propertyId) =>
        listCompanyPropertyLandParcels(cid, propertyId),
      createLandParcel: (propertyId, payload) =>
        createCompanyPropertyLandParcel(cid, propertyId, payload),
      patchLandParcel: (propertyId, parcelId, payload) =>
        patchCompanyPropertyLandParcel(cid, propertyId, parcelId, payload),
      deleteLandParcel: (propertyId, parcelId) =>
        deleteCompanyPropertyLandParcel(cid, propertyId, parcelId),
    },
  };
}
